import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { FaUsers, FaQuestion, FaLeaf, FaPoundSign, FaFileAlt, FaUserFriends } from 'react-icons/fa';

// Forest group setting image
const heroBackground = 'https://images.unsplash.com/photo-1516214104703-d870798883c5?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80';

const GroupTherapyContainer = styled.div`
  color: ${theme.colors.textLight};
`;

const HeroSection = styled.div`
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5),
    rgba(${theme.colors.primaryRGB}, 0.8)
  ), url(${heroBackground}) no-repeat center center;
  background-size: cover;
  height: 90vh; 
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 20px;
  
  @media screen and (max-width: 768px) {
    height: 80vh;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin-top: 80px;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: bold;
  color: #fff;
  margin-bottom: 24px;
  letter-spacing: 1px;
  font-family: ${theme.fonts.heading};
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  
  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroTagline = styled.p`
  font-size: 1.5rem;
  color: ${theme.colors.light};
  margin: 0 auto 40px;
  font-style: italic;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
  
  @media screen and (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ContentSection = styled.section`
  background: ${theme.colors.dark};
  padding: ${theme.spacing.section} 0;
`;

const SectionWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.accent};
  margin-bottom: 40px;
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -10px;
    left: 0;
  }
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${theme.colors.textLight};
  margin-bottom: 25px;
`;

const Emphasis = styled.em`
  display: block;
  font-size: 1.2rem;
  margin: 30px 0;
  color: ${theme.colors.accent};
`;

// New component for image and text layout
const ContentWithImage = styled.div`
  display: flex;
  align-items: center;
  margin: 50px 0;
  flex-direction: ${props => props.imageRight ? 'row-reverse' : 'row'};
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  padding: ${props => props.imageRight ? '0 0 0 30px' : '0 30px 0 0'};
  
  @media screen and (max-width: 768px) {
    padding: 0;
    margin-bottom: 30px;
    width: 100%;
  }
`;

const ContentImage = styled.img`
  width: 100%;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  object-fit: cover;
  height: 300px;
`;

const TextContent = styled.div`
  flex: 1;
`;

const IncludedContainer = styled.div`
  margin: 50px 0;
  background: ${theme.colors.primaryDark};
  padding: 30px;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
`;

const IncludedTitle = styled.h3`
  font-size: 1.8rem;
  color: ${theme.colors.accent};
  margin-bottom: 25px;
  text-align: center;
`;

const IncludedList = styled.ul`
  list-style: none;
  padding: 0;
`;

const IncludedItem = styled.li`
  margin-bottom: 15px;
  font-size: 1.1rem;
  padding-left: 30px;
  position: relative;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${theme.colors.accent};
  }
`;

const EthosStatement = styled.div`
  text-align: center;
  padding: 20px;
  margin: 40px 0;
  border-top: 1px solid ${theme.colors.accent};
  border-bottom: 1px solid ${theme.colors.accent};
`;

const EthosText = styled.p`
  font-size: 1.3rem;
  font-style: italic;
  color: ${theme.colors.accent};
  line-height: 1.8;
`;

const BookButton = styled.a`
  display: inline-block;
  background: ${theme.colors.accent};
  color: white;
  padding: 15px 30px;
  font-size: 1.1rem;
  border-radius: ${theme.borderRadius};
  text-decoration: none;
  margin: 30px 0;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.accentLight};
    transform: translateY(-3px);
  }
`;

const FAQSection = styled.section`
  background: linear-gradient(to bottom, ${theme.colors.primary}, ${theme.colors.primaryDark});
  padding: ${theme.spacing.section} 0;
`;

const FAQContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FAQTitle = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.textLight};
  margin-bottom: 50px;
  text-align: center;
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const FAQItem = styled.div`
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius};
  padding: 25px;
  backdrop-filter: blur(5px);
`;

const FAQQuestion = styled.h3`
  font-size: 1.3rem;
  color: ${theme.colors.accent};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 15px;
  }
`;

const FAQAnswer = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
`;

const GroupWildernessTherapySection = () => {
  return (
    <GroupTherapyContainer id="group-wilderness-therapy">
      <HeroSection>
        <HeroContent>
          <HeroTitle>Group Wilderness Therapeutic Intervention</HeroTitle>
          <HeroTagline>For schools, charities, and organisations</HeroTagline>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <SectionWrapper>
          <SectionHeading>4–8 week therapeutic programmes in nature</SectionHeading>
          
          <Paragraph>
            These are not your typical interventions.
          </Paragraph>
          
          <Paragraph>
            Our group programmes bring people into the wild. Not just for fresh air, but for real, lasting change. 
            Rooted in trauma-informed care and designed around the needs of your group, each programme supports 
            emotional intelligence, self-esteem, connection, and wellbeing.
          </Paragraph>
          
          <ContentWithImage imageRight={true}>
            <ImageContainer imageRight={true}>
              <ContentImage 
                src={`${process.env.PUBLIC_URL}/images/group-therapy.jpg`}
                alt="Group therapy in nature" 
              />
            </ImageContainer>
            <TextContent>
              <Paragraph>
                We combine talking circles, bushcraft, natural movement, and reflective activities in a way that meets 
                people where they're at. Whether it's young people struggling in school, adults facing mental health 
                challenges, or teams in need of reconnection we create bespoke, person-centred and authentic experiences 
                that invite people to be their most authentic selves.
              </Paragraph>
            </TextContent>
          </ContentWithImage>
          
          <IncludedContainer>
            <IncludedTitle>What's Included:</IncludedTitle>
            <IncludedList>
              <IncludedItem>Initial consultation to explore your group's needs</IncludedItem>
              <IncludedItem>Bespoke programme design (typically 4–8 weeks)</IncludedItem>
              <IncludedItem>Delivery by an experienced facilitator (DBS checked, trauma-informed, insured)</IncludedItem>
              <IncludedItem>Activities may include fire-making, shelter-building, storytelling, nature walks, mindfulness, movement, creative tasks and more. There is menu to choose from</IncludedItem>
              <IncludedItem>Final session review & optional impact report</IncludedItem>
            </IncludedList>
          </IncludedContainer>
          
          <Paragraph>
            We've designed programmes for the NHS, local councils, schools, and mental health charities — always with the same ethos:
          </Paragraph>
          
          <EthosStatement>
            <EthosText>
              Nature as co-facilitator. Community as medicine. Every voice matters.
            </EthosText>
          </EthosStatement>
          
          <div style={{ textAlign: 'center' }}>
            <BookButton href="#contact">Enquire About Group Programmes</BookButton>
          </div>
        </SectionWrapper>
      </ContentSection>
      
      <FAQSection>
        <FAQContainer>
          <FAQTitle>Frequently Asked Questions</FAQTitle>
          
          <FAQItem>
            <FAQQuestion>
              <FaUsers /> Who are these programmes for?
            </FAQQuestion>
            <FAQAnswer>
              We work with schools (primary, secondary, and alternative provision), mental health charities, 
              housing support services, NHS and other community organisations. Each programme is designed to 
              suit the specific age group, needs, and goals of the participants.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaUserFriends /> How many people can take part?
            </FAQQuestion>
            <FAQAnswer>
              Groups typically range from 6–8 participants, though this can be adjusted based on needs and staff ratios.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaLeaf /> Do we need access to woodland or nature spaces?
            </FAQQuestion>
            <FAQAnswer>
              Not necessarily. If you have access to a local green space, great - if not, we can help find a suitable 
              outdoor setting. We also offer portable setups in school grounds or nearby community areas.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaQuestion /> What kind of outcomes can we expect?
            </FAQQuestion>
            <FAQAnswer>
              Common outcomes include increased self-esteem, better emotional regulation, improved group cohesion, 
              reduced anxiety, and stronger communication skills. We also see an increase in confidence and 
              connection to nature - all of which supports wider wellbeing.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaPoundSign /> How much does it cost?
            </FAQQuestion>
            <FAQAnswer>
              Costs vary depending on the group size, location, and length of programme. Please get in touch to 
              discuss options and we'll tailor something that works for your budget.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaFileAlt /> Can we see an example programme?
            </FAQQuestion>
            <FAQAnswer>
              Absolutely. Once we've had a brief call to explore your needs, I can share sample outlines or 
              references from previous work.
            </FAQAnswer>
          </FAQItem>
        </FAQContainer>
      </FAQSection>
    </GroupTherapyContainer>
  );
};

export default GroupWildernessTherapySection;